import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Form, Input, Radio, Row, Select, AutoComplete } from 'antd';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useForm } from 'antd/es/form/Form';
import LoanLayout from '../LoanLayout';
import { useLoan } from '../../stores/store';
import Loading from '../Loading';
import ButtonNav from '../../components/ButtonNav/ButtonNav';
import { stepFinished } from '../../nav';
import ManualLabel from '../../components/ManualLabel/ManualLabel';
import PlaceholderLabel from '../../components/PlaceholderLabel/PlaceholderLabel';
import { rowLayout } from '../layout';
import TradeInForm from '../components/TradeInForm';
import { PhoneInput } from '../components/PhoneInput';
import { createSchemaValidator } from '../../validators/schemaValidator';
import { pickFields } from './meta';
import AppFormItem from '../components/AppFormItem';
import { FinanceTimeFrameType, LoanPurpose, LoanType, SellerType } from '../../schema';
import { StepFooter } from '../components/StepFooter/StepFooter';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { FullAppStylingFlag } from '../../featureFlags';
import { ReactComponent as SelectArrowIcon } from '../../icons/select-arrow-icon.svg';
import { useSellerAccountList, SellerAccountRecord } from '../../hooks/useSellerAccountList';

const formItemLayout = { labelCol: { span: 0 }, wrapperCol: { span: 24 } };
const largeThird = { xs: 24, sm: 24, md: 8, lg: 8, xl: 8 };
const largeHalf = { xs: 24, sm: 24, md: 12, lg: 12, xl: 12 };
const schemaValidator = createSchemaValidator(pickFields('seller'));
const MAX_DEALER_OPTIONS = 5;

function financeAdvantageLabel(name: string, isFinanceAdvantage?: boolean) {
  return (
    <span>
      {name}
      {isFinanceAdvantage && (
        <span style={{ marginLeft: 2, fontSize: '0.95em', color: '#1890ff' }}>*</span>
      )}
    </span>
  );
}

function parsePartyId(value: string | number | null | undefined): number | undefined {
  if (value == null || value === '') return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
}

function sellerFieldsFromRecord(record: SellerAccountRecord) {
  return {
    sellerName: record.Name,
    sellerAccountId: record.Id,
    sellerPartyId: parsePartyId(record.Party_ID__c),
    sellerFinanceAdvantage: record.isFinanceAdvantage ?? undefined,
  };
}

const SellerStep: React.FC = observer(() => {
  const loanStore = useLoan();
  const { loan } = loanStore;
  const [form] = useForm();
  const isNewFunnelStyling = !!useFeatureFlags()[FullAppStylingFlag.name];
  const [dealerSearch, setDealerSearch] = useState('');
  const sellerAccounts = useSellerAccountList(true);
  const { records, loading: sellerLoading } = sellerAccounts;
  const autoMatchTriggered = useRef(false);

  useEffect(() => {
    autoMatchTriggered.current = false;
  }, [loan?.id]);

  useEffect(() => {
    if (autoMatchTriggered.current) return;
    const hasBoatInfo = loan?.sellerName || loan?.sellerPartyId != null;
    if (!loan || !hasBoatInfo || !records.length || loan.sellerAccountId) return;
    const matched = sellerAccounts.matchSellerFromBoatInfo({
      sellerName: loan.sellerName,
      partyId: loan.sellerPartyId ?? undefined,
    });
    if (matched) {
      autoMatchTriggered.current = true;
      const fields = sellerFieldsFromRecord(matched);
      runInAction(() => {
        loan.sellerName = fields.sellerName;
        loan.sellerAccountId = fields.sellerAccountId;
        loan.sellerPartyId = fields.sellerPartyId;
        loan.sellerFinanceAdvantage = fields.sellerFinanceAdvantage;
      });
      form.setFieldsValue(fields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-match runs once per loan load; no MobX observables in deps
  }, [loan?.id, records.length]);

  if (!loan || loanStore.status === 'pending') return <Loading />;

  const onFinish = () => {
    // noinspection JSIgnoredPromiseFromCall
    stepFinished();
  };

  const dealerOptions = useMemo(() => {
    const searchLower = dealerSearch.trim().toLowerCase();
    const filtered = searchLower
      ? records.filter((record) => (record.Name?.toLowerCase() ?? '').includes(searchLower))
      : records;
    return filtered.slice(0, MAX_DEALER_OPTIONS).map((record) => ({
      value: record.Name,
      label: financeAdvantageLabel(record.Name, record.isFinanceAdvantage),
      id: record.Id,
    }));
  }, [records, dealerSearch]);

  const applySellerToLoanAndForm = useCallback(
    (record: SellerAccountRecord) => {
      const currentLoan = loanStore.loan;
      if (!currentLoan) return;
      const fields = sellerFieldsFromRecord(record);
      runInAction(() => {
        currentLoan.sellerName = fields.sellerName;
        currentLoan.sellerAccountId = fields.sellerAccountId;
        currentLoan.sellerPartyId = fields.sellerPartyId;
        currentLoan.sellerFinanceAdvantage = fields.sellerFinanceAdvantage;
      });
      form.setFieldsValue(fields);
    },
    [form, loanStore]
  );

  const handleDealerSelect = useCallback(
    (value: string, option?: unknown) => {
      const optionId = (option as { id?: string } | undefined)?.id;
      const record = optionId
        ? sellerAccounts.findSellerById(optionId)
        : sellerAccounts.findSellerByName(value);
      if (record) applySellerToLoanAndForm(record);
    },
    [sellerAccounts, applySellerToLoanAndForm]
  );

  const handleValuesChange = useCallback(
    (changed: Record<string, unknown>, all: Record<string, unknown>) => {
      const currentLoan = loanStore.loan;
      if (!currentLoan) return;
      runInAction(() => {
        Object.keys(changed).forEach((key) => {
          if (key in currentLoan) (currentLoan as Record<string, unknown>)[key] = all[key];
        });
        if ('sellerName' in changed && changed.sellerAccountId === undefined) {
          currentLoan.sellerAccountId = undefined;
          currentLoan.sellerPartyId = undefined;
          currentLoan.sellerFinanceAdvantage = undefined;
          form.setFieldsValue({
            sellerAccountId: undefined,
            sellerPartyId: undefined,
            sellerFinanceAdvantage: undefined,
          });
        }
      });
    },
    [form, loanStore]
  );

  let sellerOptions = [
    { label: 'Dealer / Broker', value: SellerType.dealer },
    { label: 'Undecided', value: SellerType.undecided },
  ];
  if (loan.loanType === LoanType.used) {
    sellerOptions = [
      { label: 'Dealer / Broker', value: SellerType.dealer },
      { label: 'Private Party', value: SellerType.private },
    ];
  } else if (loan.loanType !== LoanType.new) {
    sellerOptions = [
      { label: 'Dealer / Broker', value: SellerType.dealer },
      { label: 'Private Party', value: SellerType.private },
      { label: 'Undecided', value: SellerType.undecided },
    ];
  }

  return (
    <LoanLayout>
      {isNewFunnelStyling && <div className="step-title">Boat info</div>}
      <h1>
        {loan?.purpose === LoanPurpose.boat
          ? `Tell us a little about the boat`
          : "Let us know who you're buying from:"}
      </h1>

      {loan?.purpose === LoanPurpose.boat && <p>Who are you buying from?</p>}
      <Form
        form={form}
        {...formItemLayout}
        initialValues={loan}
        onFinish={onFinish}
        onValuesChange={handleValuesChange}
      >
        <AppFormItem
          name="financingTimeFrame"
          required
          rules={[
            { required: true, message: 'Please select a Financing Timeframe' },
            schemaValidator('financingTimeFrame', loan),
          ]}
          validateTrigger="onChange"
        >
          <PlaceholderLabel label="Financing Timeframe">
            <Select allowClear suffixIcon={<SelectArrowIcon />}>
              {Object.entries(FinanceTimeFrameType).map(([key, value]) => (
                <Select.Option key={key} value={key}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </PlaceholderLabel>
        </AppFormItem>

        {loan.loanType !== LoanType.new && (
          <Col {...largeHalf}>
            <AppFormItem
              name="seller"
              className="manual-label"
              required
              hasFeedback={false}
              rules={[
                { required: true, message: 'Please select one' },
                schemaValidator('seller', loan),
              ]}
              wrapperCol={{ span: 24, offset: 0 }}
            >
              <ManualLabel label="Who are you buying from?" twoLine>
                <Radio.Group
                  optionType="button"
                  disabled={loan.loanType === LoanType.refinance}
                  options={sellerOptions}
                />
              </ManualLabel>
            </AppFormItem>
          </Col>
        )}

        {loan.seller && loan.seller !== SellerType.undecided && loan.seller !== SellerType.dealer && (
          <Row {...rowLayout}>
            <div className="mobileSpacing" />

            <Col {...largeThird}>
              <AppFormItem
                name="sellerName"
                rules={[schemaValidator('sellerName', loan)]}
                hasFeedback
              >
                <PlaceholderLabel label="Seller's Name" htmlFor="sellerName">
                  <Input />
                </PlaceholderLabel>
              </AppFormItem>
            </Col>

            <div className="mobileSpacing" />

            <Col {...largeThird}>
              <AppFormItem name="sellerPhone" rules={[schemaValidator('sellerPhone', loan)]}>
                <PlaceholderLabel label="Seller's Phone" htmlFor="sellerPhone">
                  <PhoneInput placeholder={isNewFunnelStyling ? 'Enter Phone' : undefined} />
                </PlaceholderLabel>
              </AppFormItem>
            </Col>

            <div className="mobileSpacing" />

            <Col {...largeThird}>
              <AppFormItem name="sellerEmail" rules={[schemaValidator('sellerEmail', loan)]}>
                <PlaceholderLabel label="Seller's Email" htmlFor="sellerEmail">
                  <Input inputMode="email" />
                </PlaceholderLabel>
              </AppFormItem>
            </Col>
          </Row>
        )}

        {loan.seller && loan.seller === SellerType.dealer && (
          <>
            <Row {...rowLayout}>
              <div className="mobileSpacing" />

              <Col {...largeHalf}>
                <AppFormItem
                  name="sellerName"
                  rules={[schemaValidator('sellerName', loan)]}
                  hasFeedback
                >
                  <PlaceholderLabel label="Dealer/Broker Name (optional)" htmlFor="sellerName">
                    <AutoComplete
                      allowClear
                      options={dealerOptions}
                      onSelect={handleDealerSelect}
                      onSearch={setDealerSearch}
                      placeholder={sellerLoading ? 'Loading...' : undefined}
                      notFoundContent={
                        dealerSearch.trim()
                          ? 'No matching dealer'
                          : sellerLoading
                          ? 'Loading...'
                          : null
                      }
                      suffixIcon={<SelectArrowIcon />}
                      filterOption={false}
                    />
                  </PlaceholderLabel>
                </AppFormItem>
              </Col>

              <div className="mobileSpacing" />

              <Col {...largeHalf}>
                <AppFormItem
                  name="sellerRepName"
                  rules={[schemaValidator('sellerRepName', loan)]}
                  hasFeedback
                >
                  <PlaceholderLabel label="Sales Rep Name (optional)" htmlFor="sellerRepName">
                    <Input />
                  </PlaceholderLabel>
                </AppFormItem>
              </Col>
            </Row>
          </>
        )}

        {loan.seller === SellerType.dealer && (
          <Row>
            <AppFormItem
              className="manual-label"
              name="hasTrade"
              required
              rules={[
                { required: true, message: 'Please select one' },
                schemaValidator('hasTrade', loan),
              ]}
              wrapperCol={{ span: 24, offset: 0 }}
              hasFeedback={false}
            >
              <ManualLabel label="Have Trade In?" twoLine>
                <Radio.Group
                  optionType="button"
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                />
              </ManualLabel>
            </AppFormItem>
          </Row>
        )}

        {loan?.hasTrade && (
          <Row>
            <TradeInForm />
          </Row>
        )}

        <AppFormItem
          required={false}
          name="sellerCollateralURL"
          rules={[schemaValidator('sellerCollateralURL', loan)]}
          hasFeedback
        >
          <PlaceholderLabel
            label="Seller's collateral listing website URL (optional)"
            htmlFor="sellerCollateralURL"
          >
            <Input />
          </PlaceholderLabel>
        </AppFormItem>

        <ButtonNav />
        <StepFooter type="info" />
      </Form>
    </LoanLayout>
  );
});

export default SellerStep;
